import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AllowedAction, AppState, OtpStatus, Tag, User, UserGroup, UserGroupRole } from './appTypes';
import { find } from 'ramda';
import type { SliceActions } from './reduxHelper';
import { useAppSelector } from '../../configuration/setup/hooks';
import type { RootState } from '../../configuration/setup/store';

export const initialState: AppState = {
    accessIsForbidden: false,
    allowedGroupActions: [],
    allowedUserActions: [],
    groups: [],
    groupsLoadMoreLink: null,
    roles: [],
    routeInfo: {},
    nextGroup: null,
    nextUser: null,
    selectedGroup: null,
    selectedUser: null,
    serverErrorCode: null,
    sessionWarningAcknowledged: false,
    users: [],
    usersLoadMoreLink: null,
    hasFetchedGroupsTabData: false,
    hasFetchedUsers: false,
    hasFetchedUsersTabData: false,
    userMfaSettingsByUserId: {},
    userMfaSettingsFetchError: false,
    isLoading: false,
    loadingError: null,
    tags: [],
    lastCreatedUserId: undefined,
};

const upsertUser = (users: User[], candidate: User): User[] => {
    const existingUserIndex = users.findIndex(user => user.id === candidate.id);

    if (existingUserIndex === -1) {
        return [...users, candidate];
    }

    return users.map(user => (user.id === candidate.id ? candidate : user));
};

const upsertGroup = (groups: UserGroup[], candidate: UserGroup): UserGroup[] => {
    const existingGroupIndex = groups.findIndex(group => group.id === candidate.id);

    if (existingGroupIndex === -1) {
        return [...groups, candidate];
    }

    return groups.map(group => (group.id === candidate.id ? candidate : group));
};

const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        groupDeleted(state, action: PayloadAction<string>) {
            state.groups = state.groups.filter(group => group.id !== action.payload);
        },

        groupDeselected(state) {
            state.selectedGroup = null;
        },

        setNextGroup(state, action: PayloadAction<{ group: UserGroup; redirect: boolean } | null>) {
            state.nextGroup = action.payload;
        },

        setNextUser(state, action: PayloadAction<{ user: User; redirect: boolean } | null>) {
            state.nextUser = action.payload;
        },

        groupSelected(state, action: PayloadAction<UserGroup>) {
            state.selectedGroup = action.payload;
        },

        groupsFetched(
            state,
            action: PayloadAction<{
                groups: UserGroup[];
                loadMoreLink: string | null;
                allowedActions: AllowedAction[] | null;
            }>
        ) {
            state.allowedGroupActions = action.payload.allowedActions
                ? action.payload.allowedActions
                : state.allowedGroupActions;
            state.groups = action.payload.groups;
            state.groupsLoadMoreLink = action.payload.loadMoreLink;
        },

        groupsTabDataFetched(state) {
            state.hasFetchedGroupsTabData = true;
        },

        rolesFetched(state, action: PayloadAction<UserGroupRole[]>) {
            state.roles = action.payload;
        },

        selectedGroupFetched(state, action: PayloadAction<UserGroup>) {
            state.selectedGroup = action.payload;
        },

        selectedUserFetched(state, action: PayloadAction<User>) {
            state.selectedUser = action.payload;
            state.users = upsertUser(state.users, action.payload);
        },

        sessionWarningDialogClosed(state) {
            state.sessionWarningAcknowledged = true;
        },

        userDeleted(state, action: PayloadAction<string>) {
            state.users = state.users.filter(user => user.id !== action.payload);
            delete state.userMfaSettingsByUserId[action.payload];
        },

        userDeselected(state) {
            state.selectedUser = null;
        },

        userSelected(state, action: PayloadAction<User>) {
            state.selectedUser = action.payload;
        },

        isLoading(state, action: PayloadAction<{ isLoading: boolean; error?: string | undefined }>) {
            state.isLoading = action.payload.isLoading;
            state.loadingError = action.payload.error;
        },

        usersFetched(
            state,
            action: PayloadAction<{
                users: User[];
                loadMoreLink: string | null;
                allowedActions: AllowedAction[] | null;
            }>
        ) {
            state.users = action.payload.users;
            state.usersLoadMoreLink = action.payload.loadMoreLink;
            state.hasFetchedUsers = true;
            state.allowedUserActions = action.payload.allowedActions
                ? action.payload.allowedActions
                : state.allowedGroupActions;
        },

        usersTabDataFetched(state) {
            state.hasFetchedUsersTabData = true;
        },

        userMfaSettingsFetched(state, action: PayloadAction<Record<string, OtpStatus>>) {
            state.userMfaSettingsByUserId = action.payload;
            state.userMfaSettingsFetchError = false;
        },

        userMfaSettingsFetchFailed(state) {
            state.userMfaSettingsByUserId = {};
            state.userMfaSettingsFetchError = true;
        },

        tagsFetched(state, action: PayloadAction<Tag[]>) {
            state.tags = action.payload;
        },

        addGroup(state, action: PayloadAction<UserGroup>) {
            state.groups = [...state.groups, action.payload];
        },

        updateGroup(state, action: PayloadAction<{ group: UserGroup }>) {
            const updatedGroups = upsertGroup(state.groups, action.payload.group);
            state.groups = updatedGroups;
            state.selectedGroup =
                state.selectedGroup?.id === action.payload.group.id
                    ? action.payload.group
                    : state.selectedGroup
                      ? (find((it: UserGroup) => it.id === state.selectedGroup?.id, updatedGroups) ??
                        state.selectedGroup)
                      : null;
        },

        updateUser(state, action: PayloadAction<User>) {
            state.selectedUser = action.payload;
            state.users = upsertUser(state.users, action.payload);
        },

        userCreated(state, action: PayloadAction<string>) {
            state.lastCreatedUserId = action.payload;
        },
    },
});

export const { sessionWarningDialogClosed } = appSlice.actions;

export type AppActionTypes = SliceActions<typeof appSlice.actions>;

const getSessionExpiredAcknowledged = (state: RootState) => state.app.sessionWarningAcknowledged;

export const useSessionExpiredAcknowledged = () => useAppSelector(getSessionExpiredAcknowledged);

export default appSlice;
